import React from 'react';
import CycleChart from '../Charts/RadialBar';
import BatteryLevel from '../Charts/Battery/BatteryLevel';
import HorizontalBarChart from '../Charts/HorizontalBar/HorizontalBar';
import UnitRegButtons from '../Charts/UnitRegButtons';
import Windspeed from '../Charts/WindSpeed';
import GasBurn from '../Charts/GasBurn';
import LineGraph from '../Charts/Line/CustomLineChart';
import Electricicon from '../../assets/ElectricIcon.png';
import { data } from 'react-router-dom';

interface DynamicCardProps {
    category: string;
    charts: Array<any>;  // Array of charts configuration
    data: {
        asset_id: string;
        icon: string;
        asset_name: string;
        ISO: string;
        RT_VOL: string;
        DA_VOL: string;
        RT_LMP: string;
        DA_LMP: string;
        cyclePercentage?: number;
        batteryLevel?: number;
        Day1_Forecast: number;
        Day2_Forecast: number;
        sunriseTime?: string;
        sunsetTime?: string;
        asset_type: string;
        unit_status: string;
        reg_status: string;
        windspeed: string;
        gasBurnValue: number;
        current_time: string;
        maxEnergy:number;
    };
}

const DynamicCard: React.FC<DynamicCardProps> = ({ category, charts, data }) => {
    // Filter charts by category
    const filteredCharts = charts.filter(chart => {
        return category === data.asset_type;
    });

    // console.log("Time and asset_ids:",data.assetTime);
    const formatCurrency = (val: any) => {
        const num = Number(val);
        if (isNaN(num)) return "-"; // handles "-", null, undefined, etc.
        return num < 0
          ? `-$${Math.abs(num).toFixed(2)}`
          : `$${num.toFixed(2)}`;
      };
    
      const isDataValid = (value: number | string | null | undefined) => {
        if (value == null) return false;       // null or undefined
        if (value === 0) return false;         // numeric zero
        if (value === "0.00" || value === "0") return false; // string zero
        return true;
    };
    
    return (
        <div className="flex h-64 max-w-full flex-col rounded-xl border border-gray-300 bg-white p-5 shadow-md md:max-w-xs lg:max-w-sm xl:max-w-md 2xl:max-w-lg">
            {/* Top Section */}
            <div className="flex items-center w-full">
                <img src={data.icon} alt="Acron Icon" className="w-6 h-6  mr-4" />
                <div className="flex flex-col w-full">
                    <h1 className="text-base md:text-sm lg:text-lg font-bold truncate w-full">
                        {data.asset_name}
                    </h1>
                    <p className="text-sm text-custom-grey mt-0.5 font-semibold">{data.ISO}</p>
                </div>
            </div>

            {/* Bottom Section: Left (Values) & Right (Charts) */}
            <div className="flex justify-between w-full">
                {/* Left Side: Values (This stays constant for all cards) */}
                <div className="w-1/2 mt-9">
                    <p className="text-sm text-gray-600">
                        <span className="font-semibold text-black-600 text-xl">{data.RT_VOL}</span>
                        <span className="ml-2 text-xs text-gray-600">Vol. RT</span>
                    </p>
                    <p className="flex items-center text-sm text-gray-600">
                        <img src={Electricicon} alt="Electric Icon" className="w-3 h-3 mr-2" />
                        <span className="text-xm text-gray-600">{data.DA_VOL}</span>
                        <span className="ml-2 text-xs text-gray-600">Vol. DA</span>
                    </p>
                    <div className="w-full h-px bg-gray-300 my-2"></div>
                    <p className="text-xl" style={{ color: '#75F84F' }}>
                    <span className="font-semibold">
                    {formatCurrency(data.RT_LMP)}
</span>

                        <span className="ml-2 text-xs text-gray-600">LMP RT</span>
                    </p>
                    <p className="text-sm text-red-500 my-1">
                    <span className="text-xm text-gray-600">
                    {formatCurrency(data.DA_LMP)}
</span>
              <span className="ml-2 text-xs text-gray-600">LMP DA</span>
                    </p>
                </div>

                {/* Right Side: Charts */}
                <div className="w-1/2 flex flex-col items-center space-y-3 mt-[-10px] 2xl:mt-[-18px]">
                
                {filteredCharts.map((chart: { type: string; value?: any }, index: number) => {
  const hideChart = (val: any): boolean => {
    if (val === null || val === undefined) return true;
    if (typeof val === "string") {
      const trimmed = val.trim();
      return trimmed === "" || trimmed === "-" || trimmed === "0" || trimmed === "0.00";
    }
    if (typeof val === "number") return val === 0 || Number.isNaN(val);
    return false;
  };

  // Use hideChart to set visibility
  const wrapperStyle = { visibility: hideChart(chart.value) ? ("hidden" as const) : ("visible" as const) };


    switch (chart.type) {
        case 'CycleChart':
            return (
                <div
                    key={index}
                    style={{
                        ...wrapperStyle,
                        visibility: hideChart(data.cyclePercentage) ? "hidden" : "visible"
                    }}
                >
                    <CycleChart percentage={data.cyclePercentage ?? 0} />
                </div>
            );

        case 'UnitRegButtons':
            return (
                <div
                    key={index}
                    style={{
                        ...wrapperStyle,
                        visibility: hideChart(data.unit_status) ? "hidden" : "visible"
                    }}
                >
                    <UnitRegButtons
                        unit_status={data.unit_status ?? 'OFF'}
                        reg_status={data.reg_status ?? 'OFF'}
                    />
                </div>
            );

        case 'BatteryLevel':
            return (
                <div
                    key={index}
                    style={{
                        ...wrapperStyle,
                        visibility: hideChart(data.batteryLevel) ? "hidden" : "visible"
                    }}
                >
                    <BatteryLevel currentEnergy={data.batteryLevel ?? 0} maxEnergy={data.maxEnergy} />
                </div>
            );

        case 'HorizontalBarChart':
            return (
                <div
                    key={index}
                    className="mt-[-5px] mb-[-10px]"
                    style={{
                        ...wrapperStyle,
                        visibility: hideChart(data.Day1_Forecast) ? "hidden" : "visible"
                    }}
                >
                    <HorizontalBarChart
                        day1Forecast={data.Day1_Forecast ?? 0}
                        day2Forecast={data.Day2_Forecast ?? 0}
                    />
                </div>
            );

            case 'LineGraph':
                return (
                  <div
                    key={index}
                    className="relative ml-9 -translate-y-[12px]"
                    style={{
                      ...wrapperStyle,
                      visibility: hideChart(data.sunriseTime) ? "hidden" : "visible"
                    }}
                  >
                    <LineGraph
                      sunriseTime={data.sunriseTime ?? ""}
                      sunsetTime={data.sunsetTime ?? ""}
                      current_time={data.current_time ?? ""}
                    />
                  </div>
                );
              

        case 'WindSpeed':
            return (
                <div
                    key={index}
                    className="mt-[10px]"
                    style={{
                        ...wrapperStyle,
                        visibility: hideChart(data.windspeed) ? "hidden" : "visible"
                    }}
                >
                    <Windspeed windspeed={data.windspeed} />
                </div>
            );

            case 'GasBurn':
                return (
                  <div key={index} className=" flex items-center justify-center">
                    {data.gasBurnValue !== null && data.gasBurnValue !== undefined && !isNaN(Number(data.gasBurnValue))
                      ? <GasBurn gasBurnValue={data.gasBurnValue} />
                      : null}
                  </div>
                );
              

        default:
            return null;
    }
})}

</div>


            </div>
        </div>

    );
};

export default DynamicCard;
